import { Component, inject } from '@angular/core';
import { deepmerge } from '@library/charts/utils';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { EnumApiScales, EnumMeasureTypes, EnumScales } from '@library/store/measures/measures.interface';
import { UserFacade } from '@library/store/user/user.facade';
import { User } from '@library/store/user/user.interface';
import { getColorValue } from '@library/utils/helpers/functions/getColorVariable';
import { ToUserUnitPipe } from '@library/utils/pipes/to-user-unit.pipe';
import { GraphTooltipService } from '@molecules/graph-tooltip/graph-tooltip.service';
import { CpForecastService } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-forecast/cp-forecast-service';
import { CpForecastRainTooltipService, RainGraphDataset, RainProbaGraphDataset } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-forecast/cp-forecast-tabs/cp-forecast-tab-rain/cp-forecast-rain-tooltip.service';
import { baseConfig } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/graph-configs/common-config';
import { AutoYMinMaxPlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/auto-y-min-max';
import { VerticalLinePlugin } from '@pages/control-page/control-page-main/control-page-dashboard/control-page-body/control-page-graph/plugins/vertical-line';
import { DATASET_BASE_OPTIONS, SCALE_UNITS } from '@pages/control-page/control-page.const';
import * as moment from 'moment';
import { combineLatest, map } from 'rxjs';

@Component({
  selector: 'app-cp-forecast-tab-rain',
  templateUrl: './cp-forecast-tab-rain.component.html',
  styleUrl: './cp-forecast-tab-rain.component.scss',
  providers: [
    { provide: GraphTooltipService, useClass: CpForecastRainTooltipService },
  ],
})
export class CpForecastTabRainComponent {

  forecast = inject(CpForecastService).forecast;

  apiScale = EnumApiScales.THREE_HOURS;

  get timeRange() {
    const rainData = this.forecast().forecastGraphs.rain;
    return {
      dateBegin: moment(rainData[0][0] * 1000),
      dateEnd: moment(rainData[rainData.length - 1][0] * 1000 + 60 * 60 * 3 * 1000),
    };
  }

  private config$ = combineLatest({
    homeId: this.homesFacade.currentHomeId$,
    user: this.userFacade.currentUser$,
  }).pipe(
    map(({user}) => this.getConfig(user)),
  );

  viewModel$ = combineLatest({
    config: this.config$,
  });

  constructor(
    private graphTooltipService: GraphTooltipService,
    private homesFacade: HomesFacade,
    private userFacade: UserFacade,
    private toUserUnitPipe: ToUserUnitPipe,
  ) {}

  private getConfig(user: User) {
    const dataRain = this.forecast().forecastGraphs.rain
      .map(([x, y]) => ({x: x * 1000, y: this.toUserUnitPipe.transform(y, EnumMeasureTypes.SUM_RAIN)}));
    const config = deepmerge(baseConfig, {
      data: {
        datasets: [
          {
            ...DATASET_BASE_OPTIONS[RainGraphDataset.type],
            data: dataRain,
            yAxisID: RainGraphDataset.yScaleType,
            borderColor: getColorValue(RainGraphDataset.color),
            backgroundColor: getColorValue(RainGraphDataset.color),
            hoverBackgroundColor: getColorValue(RainGraphDataset.color),
          },
          {
            ...DATASET_BASE_OPTIONS[RainProbaGraphDataset.type],
            data: this.forecast().forecastGraphs.rain_proba.map(([x, y]) => ({x: x * 1000, y})),
            yAxisID: RainProbaGraphDataset.yScaleType,
            borderColor: getColorValue(RainProbaGraphDataset.color),
            backgroundColor: getColorValue(RainProbaGraphDataset.color),
            hoverBackgroundColor: getColorValue(RainProbaGraphDataset.color),
          },
        ],
      },
      options: {
        scales: {
          x: {type: EnumScales.WEEK, stacked: true},
        },
        plugins: {
          tooltip: this.graphTooltipService.tooltip,
          'auto-y-min-max': {
            unitSystem: user.unit_system ?? 0,
            unitPressure: user.unit_pressure ?? 0,
            unitWind: user.unit_wind ?? 0,
          }
        }
      },
      plugins: [AutoYMinMaxPlugin, VerticalLinePlugin]
    });

    delete config.options.scales.y;

    const yScales = [...new Set([RainGraphDataset, RainProbaGraphDataset].map(m => m.yScaleType))];
    yScales.forEach((type, i) => {
      config.options.scales[type] = {
        type: type,
        position: i % 2 === 0 ? 'left' : 'right',
        grid: { drawOnChartArea: i === 0 },
        ticks: {
          callback: function (val) {
            return `${this.getLabelForValue(val)} ${SCALE_UNITS[this.type](user)}`;
          }
        }
      };
    });

    return config;
  }

}
